import { useState, useContext, useEffect, isValidElement, FC, PropsWithChildren, ReactNode } from 'react';
import Head from 'next/head';
import { SkipNavLink, SkipNavContent } from '@reach/skip-nav';
import Confetti from 'react-confetti';
import TopNav from '@components/TopNav/TopNav';
import Notifications from '@components/Notifications/Notifications';
import Overlay from '@components/Overlay/Overlay';

import TourComponent from '@components/Tour';

import { analytics } from '@modules/analytics';
import useScreenCategory from '@hooks/use-screen-category';
import { filter } from '@modules/react-children-utilities';
import { layoutContext } from '@context/layout';
import { upgradeFlowContext } from '@context/upgrade-flow-context';

import { useUser } from '@context/user-context';
import { TourContext } from '@context/tour-context';
import { BusinessOnboardingStepGroups, BusinessOnboardingSteps } from '@modules/onboarding';
import { ProgressChecklistContext } from '@context/progress-checklist-context';

import styles from './Layout.module.scss';
import useWindowSize from '@hooks/use-window-size';
import { isStepCompleted as isChecklistStepCompleted } from '@hooks/use-progress-checklist';
import { isStepActive as isTourStepActive } from '@hooks/use-tour';

import SideNav from '@components/SideNav/SideNav';

import { TeamAdminUnsupportedBreakpointOverlay } from '@components/UnsupportedBreakpointOverlays/TeamAdminOverlay';
import { IndividualUserUnspportedBreakpointOverlay } from '@components/UnsupportedBreakpointOverlays/IndividualUserOverlay';

import { useTeamRole } from '@hooks/team-workspace';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { datadogRum } from '@datadog/browser-rum';
import { makePageTitle } from '@modules/page-title';

interface LayoutProps {
  title?: string;
  children: ReactNode;
}

const Layout: FC<LayoutProps> = ({ children, title = 'Blinq' }) => {
  const { state: navState, dispatch: navDispatch } = useContext(layoutContext);
  const { tourState, tourSteps } = useContext(TourContext);
  const {
    dispatch: checklistDispatch,
    steps: checklistSteps,
    show: showChecklist,
  } = useContext(ProgressChecklistContext);

  const [, isMedium, isLarge] = useScreenCategory();
  const { loading: loadingUserDetails, ...userDetails } = useUser();
  const { user } = userDetails;

  const { planName } = useActorPlan();
  const { atLeastRole, loading: teamRoleLoading } = useTeamRole();
  const isTeamAdmin = atLeastRole('ADMIN');

  const topNav = filter(children, (child) => isValidElement(child) && child?.type === TopNav) ?? TopNav;
  const content = filter(children, (child) => isValidElement(child) && child?.type !== TopNav);

  const showCelebration =
    isChecklistStepCompleted(checklistSteps, BusinessOnboardingStepGroups.CreateCard) &&
    isTourStepActive(tourSteps, BusinessOnboardingSteps.ModifyTemplate);

  // Once the final step in the checklist is completed we can finish the checklist (which will remove it) and
  // prevent the onboarding flow from appearing again (this isn't currently needed but in the future when I figure
  // out how to have a robust tutorial flow it should be in here)
  useEffect(() => {
    if (
      isChecklistStepCompleted(checklistSteps, BusinessOnboardingStepGroups.ModifyTemplate) &&
      user &&
      showChecklist
    ) {
      checklistDispatch({
        type: 'FINISH',
      });
    }
  }, [tourState, user, checklistDispatch, checklistSteps, showChecklist]);

  useEffect(() => {
    // The minimized state is undefined on first render so we explicitly check that it's false
    if (navState.minimized === false) document.body.classList.add('no-scroll');
    else document.body.classList.remove('no-scroll');
  }, [navState.minimized]);

  useEffect(() => {
    if (isMedium && !navState.overridden) navDispatch({ type: 'COLLAPSE' });
    else if (isMedium) navDispatch({ type: 'MINIMIZE' });
  }, [isMedium, navState.overridden, navDispatch]);

  useEffect(() => {
    if (isLarge && !navState.overridden) navDispatch({ type: 'EXPAND' });
  }, [isLarge, navState.overridden, navDispatch]);

  useEffect(() => {
    if (!user || !user.signInEmailAddress || process.env.NODE_ENV !== 'production') return;

    const isNewTemplatesEnabled = true;
    const isNewScimEnabled = true;

    const cardsApi = isNewTemplatesEnabled ? 'graphql' : 'firestore';

    const releasePhase = isNewScimEnabled ? 2 : 1;

    datadogRum.setUser({
      id: user.userId,
      email: user.signInEmailAddress,
      name: `${user.firstName} ${user.lastName}`,
    });

    analytics.identify({
      user_id: user.userId,
      traits: { card_api_source: cardsApi, graphql_cards_api_release_phase: releasePhase },
    });
  }, [user, planName]);

  // Inject Zendesk widget into the web page once the component is mounted.
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=c727bcfe-4bd6-4ac6-8e57-99db4fb9116d';
    script.id = 'ze-snippet';
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const pageTitle = makePageTitle(title);
  return (
    <upgradeFlowContext.Provider value={{ flowType: 'in-app' }}>
      {showCelebration && <Celebration />}
      <TourComponent />
      <div className={styles.layout}>
        <Head>
          <title>{pageTitle}</title>
        </Head>

        {/* Unsupported Breakpoint Overlays */}
        {teamRoleLoading ? null : isTeamAdmin ? (
          <TeamAdminUnsupportedBreakpointOverlay />
        ) : (
          <IndividualUserUnspportedBreakpointOverlay />
        )}

        <SkipNavLink />

        <Overlay
          show={!navState.minimized}
          id="mobile-nav-overlay"
          onClick={() => navDispatch({ type: 'MINIMIZE' })}
          className={styles['mobile-overlay']}
        >
          <SideNav />
        </Overlay>

        <div className={styles.contents} data-collapsed={navState.collapsed}>
          {topNav}
          <SkipNavContent />

          <main className={styles['main-content']}>{content}</main>

          <div className={styles['notifications-section']}>
            <Notifications />
          </div>
        </div>
      </div>
    </upgradeFlowContext.Provider>
  );
};

const Celebration: FC<PropsWithChildren<unknown>> = () => {
  const { width, height } = useWindowSize();
  const [show, setShow] = useState(false);

  // Only render on the client
  useEffect(() => {
    setShow(true);
  }, []);

  if (!show) return null;
  else return <Confetti width={width} height={height} style={{ zIndex: 10 }} />;
};

export default Layout;
